import { LOG_TRACKING, isBloomreachEnabled } from '../../../../config/config';
import Analytics from '../../Analytics';
import AnalyticsQueue from '../../AnalyticsQueue/AnalyticsQueue';
import { ExponeaEvents, ExponeaEventDataMap } from './types/Events';

// Interface that's applied to window.exponea.
export interface Exponea {
    start: () => void;
    track: <K extends ExponeaEvents>(event: K, data: ExponeaEventDataMap[K]) => void;
}

type QueueItem = {
    [K in ExponeaEvents]: { queueEventName: K } & ExponeaEventDataMap[K];
}[ExponeaEvents];

/**
 * Class to manage bloomreach analytics actions.
 * Main script is loaded in via gatsby-ssr config file.
 * @see https://documentation.bloomreach.com/engagement/docs/js-sdk
 */
class ExponeaAnalytics extends Analytics {
    public hasExponeaBeenStarted = false;
    readonly isEnabled = () => (
        isBloomreachEnabled() && this.isAnalyticsEnabled()
    );

    private queue = new AnalyticsQueue<QueueItem>('exponea', (item) => {
        if (typeof window === 'undefined') {
            return;
        }

        const { queueEventName, ...rest } = item;

        window.exponea?.track(queueEventName, {
            ...rest
        });
    });

    // Runs the Exponea start script.
    readonly startExponea = () => {
        // Checks to see if we have access to the exponea start method.
        const startExponea = typeof window !== 'undefined' ? window.exponea?.start : null;

        if (!startExponea) {
            return false;
        }

        if (this.hasExponeaBeenStarted) {
            return false;
        }

        if (!this.isEnabled()) {
            return false;
        }

        startExponea();
        this.queue.process();
        this.hasExponeaBeenStarted = true;

        return true;
    };

    readonly dispatchEvent = <K extends ExponeaEvents>(event: K, data: ExponeaEventDataMap[K]) => {
        // If log tracking in the .env is enabled.
        if (LOG_TRACKING) {
            console.log('Exponea Event:', event, data);
        }

        if (this.isEnabled() && window.exponea) {
            window.exponea.track(event, data);
            return;
        }

        this.queue.add({
            queueEventName: event,
            ...data
        });
    }
}

export default new ExponeaAnalytics();
